import { useMemo } from "react";
import styles from "./BetSlider.module.css";
import { formatUsd } from "../lib/format";
import { useHaptic } from "../hooks/useHaptic";

type Props = {
  value: number;
  balance: number;
  onChange: (v: number) => void;
  disabled?: boolean;
};

const PRESETS = [25, 50, 75, 100] as const;
const MIN_BET = 1;

export function BetSlider({ value, balance, onChange, disabled = false }: Props) {
  const haptic = useHaptic();
  const max = Math.max(MIN_BET, Math.floor(balance));
  const pct = useMemo(() => {
    if (max <= MIN_BET) return 0;
    return ((value - MIN_BET) / (max - MIN_BET)) * 100;
  }, [value, max]);

  const handleSlide = (raw: number) => {
    onChange(Math.max(MIN_BET, Math.min(Math.round(raw), max)));
  };

  const handlePreset = (p: (typeof PRESETS)[number]) => {
    haptic.select();
    const v = Math.max(MIN_BET, Math.round((p / 100) * max));
    onChange(v);
  };

  const activePreset =
    PRESETS.find((p) => Math.abs(value - (p / 100) * max) < 0.5) ?? null;

  return (
    <div className={`${styles.root} ${disabled ? styles.disabled : ""}`}>
      <div className={styles.row}>
        <span className={styles.amount}>{formatUsd(value)}</span>
        <span className={styles.balance}>
          Balance
          <span className={styles.balanceValue}>{formatUsd(balance)}</span>
        </span>
      </div>

      <div className={styles.sliderHost}>
        <div className={styles.track}>
          <div className={styles.fill} style={{ width: `${pct}%` }} />
        </div>
        <div className={styles.thumb} style={{ left: `${pct}%` }} />
        <input
          type="range"
          className={styles.input}
          min={MIN_BET}
          max={max}
          step={1}
          value={value}
          onChange={(e) => handleSlide(Number(e.target.value))}
          disabled={disabled}
          aria-label="Bet amount"
        />
      </div>

      <div className={styles.dots}>
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            className={`${styles.dot} ${
              activePreset === p ? styles.dotActive : ""
            }`}
            onClick={() => handlePreset(p)}
            disabled={disabled}
          >
            {p === 100 ? "ALL IN" : `${p}%`}
          </button>
        ))}
      </div>
    </div>
  );
}
